import api from "@/lib/api";

// Enable stereo, hi-fi Opus so music-mode callers come through rich and wide.
function preferStereoOpus(sdp) {
  try {
    if (!sdp) return sdp;
    const lines = sdp.split("\r\n");
    let pt = null;
    for (const l of lines) { const m = l.match(/^a=rtpmap:(\d+)\s+opus\/48000/i); if (m) { pt = m[1]; break; } }
    if (!pt) return sdp;
    const params = "stereo=1;sprop-stereo=1;maxaveragebitrate=128000;maxplaybackrate=48000;useinbandfec=1";
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith(`a=fmtp:${pt}`)) {
        if (!/stereo=1/.test(lines[i])) lines[i] += ";" + params;
        return lines.join("\r\n");
      }
    }
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith(`a=rtpmap:${pt} opus`)) { lines.splice(i + 1, 0, `a=fmtp:${pt} ${params}`); break; }
    }
    return lines.join("\r\n");
  } catch (e) { return sdp; }
}

let cachedIce = null;
async function iceConfig() {
  if (cachedIce) return cachedIce;
  try {
    const { data } = await api.get("/live/ice-config");
    cachedIce = { iceServers: data.iceServers };
  } catch (e) {
    cachedIce = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };
  }
  return cachedIce;
}

// Host: one broadcaster -> many listeners (mesh) via HTTP-polled signaling.
export function createBroadcaster(sid, stream) {
  const listeners = new Map();
  let after = null, stopped = false, onCount = () => {};

  async function handleOffer(lid, offer) {
    const pc = new RTCPeerConnection(await iceConfig());
    listeners.set(lid, pc);
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) api.post(`/live/${sid}/signal`, { to: "listener", listener_id: lid, from: "host", type: "ice", payload: e.candidate.toJSON() });
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) { pc.close(); listeners.delete(lid); onCount(listeners.size); }
    };
    await pc.setRemoteDescription(offer);
    const ans = await pc.createAnswer();
    const ansSdp = preferStereoOpus(ans.sdp);
    await pc.setLocalDescription({ type: ans.type, sdp: ansSdp });
    await api.post(`/live/${sid}/signal`, { to: "listener", listener_id: lid, from: "host", type: "answer", payload: { type: ans.type, sdp: ansSdp } });
    onCount(listeners.size);
  }

  async function poll() {
    if (stopped) return;
    try {
      const url = `/live/${sid}/signal?to=host` + (after ? `&after=${after}` : "");
      const { data } = await api.get(url);
      for (const s of data) {
        after = s.id;
        if (s.type === "offer") await handleOffer(s.listener_id, s.payload);
        else if (s.type === "ice" && listeners.has(s.listener_id)) { try { await listeners.get(s.listener_id).addIceCandidate(s.payload); } catch (e) {} }
      }
    } catch (e) {}
    if (!stopped) setTimeout(poll, 1500);
  }
  poll();
  return { setOnCount(fn) { onCount = fn; }, replaceTrack(newTrack) { if (!newTrack) return; listeners.forEach((pc) => { try { const s = pc.getSenders().find((x) => x.track && x.track.kind === "audio"); if (s) s.replaceTrack(newTrack).catch(() => {}); } catch (e) {} }); }, stop() { stopped = true; listeners.forEach((pc) => pc.close()); listeners.clear(); } };
}

// Listener: receive host audio.
export function createListener(sid, audioEl, onState) {
  const lid = Math.random().toString(36).slice(2);
  let after = null, stopped = false, pc = null;

  async function start() {
    pc = new RTCPeerConnection(await iceConfig());
    pc.ontrack = (e) => { audioEl.srcObject = e.streams[0]; audioEl.play().catch(() => {}); onState && onState("playing"); };
    pc.onicecandidate = (e) => { if (e.candidate) api.post(`/live/${sid}/signal`, { to: "host", listener_id: lid, from: "listener", type: "ice", payload: e.candidate.toJSON() }); };
    pc.addTransceiver("audio", { direction: "recvonly" });
    const offer = await pc.createOffer();
    const offerSdp = preferStereoOpus(offer.sdp);
    await pc.setLocalDescription({ type: offer.type, sdp: offerSdp });
    await api.post(`/live/${sid}/signal`, { to: "host", listener_id: lid, from: "listener", type: "offer", payload: { type: offer.type, sdp: offerSdp } });
    poll();
  }
  async function poll() {
    if (stopped || !pc) return;
    try {
      const url = `/live/${sid}/signal?to=listener&listener_id=${lid}` + (after ? `&after=${after}` : "");
      const { data } = await api.get(url);
      for (const s of data) {
        after = s.id;
        if (s.type === "answer") await pc.setRemoteDescription(s.payload);
        else if (s.type === "ice") { try { await pc.addIceCandidate(s.payload); } catch (e) {} }
      }
    } catch (e) {}
    if (!stopped) setTimeout(poll, 1500);
  }
  start();
  return { stop() { stopped = true; if (pc) pc.close(); } };
}


// Host: mixed live studio = host mic + any callers, broadcast as one stream.
export async function createLiveStudio(sid, micStream) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  try { await ctx.resume(); } catch (e) {}
  const dest = ctx.createMediaStreamDestination();
  const micSource = ctx.createMediaStreamSource(micStream);
  let activeMicSource = micSource;
  let activeMicStream = micStream;
  // Mic level control: micSource -> micGain -> broadcast delay chain -> dest
  const micGain = ctx.createGain();
  micGain.gain.value = 1;
  micSource.connect(micGain);
  // Bus for all callers, so we can meter and auto-duck against caller speech.
  const callersBus = ctx.createGain();
  callersBus.gain.value = 1;
  // Broadcast (profanity) delay: mic + callers -> delay -> broadcastGain -> dest.
  // Monitor/analysers tap micGain/callersBus BEFORE the delay so the host hears realtime.
  const broadcastDelay = ctx.createDelay(20);
  broadcastDelay.delayTime.value = 0;
  const broadcastGain = ctx.createGain();
  broadcastGain.gain.value = 1;
  micGain.connect(broadcastDelay);
  const callersBroadcastGain = ctx.createGain();
  callersBroadcastGain.gain.value = 1; // 0 = caller off the broadcast (private off-air), 1 = on air
  callersBus.connect(callersBroadcastGain);
  callersBroadcastGain.connect(broadcastDelay);
  broadcastDelay.connect(broadcastGain);
  broadcastGain.connect(dest);
  // Analyser for the live input level meter (reads input, works on iOS too)
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  micGain.connect(analyser);
  const _levelBuf = new Uint8Array(analyser.frequencyBinCount);
  // Analyser on the caller bus for auto-duck speech detection.
  const callerAnalyser = ctx.createAnalyser();
  callerAnalyser.fftSize = 256;
  callersBus.connect(callerAnalyser);
  const _callerBuf = new Uint8Array(callerAnalyser.frequencyBinCount);
  // Master analyser (mic + callers) for peak / clip history.
  const masterAnalyser = ctx.createAnalyser();
  masterAnalyser.fftSize = 256;
  micGain.connect(masterAnalyser);
  callersBus.connect(masterAnalyser);
  const _mixBuf = new Uint8Array(masterAnalyser.frequencyBinCount);
  function _peak(anal, buf) {
    anal.getByteTimeDomainData(buf);
    let p = 0;
    for (let i = 0; i < buf.length; i++) { const d = Math.abs(buf[i] - 128); if (d > p) p = d; }
    return Math.min(1, p / 128);
  }
  // Mic gain = base level (host slider) * duck multiplier (auto-duck) * gate multiplier.
  let baseMic = 1, duckMul = 1, autoDuck = true, gateMul = 1;
  function applyMic(smooth = 0.08) {
    const g = Math.max(0, baseMic * duckMul * gateMul);
    try { micGain.gain.setTargetAtTime(g, ctx.currentTime, smooth); } catch (e) { try { micGain.gain.value = g; } catch (x) {} }
  }
  // Gentle noise gate: measured on the RAW mic (pre-gain) so gating doesn't feed back
  // on itself. Stays open while the host speaks, closes softly between sentences.
  const preGainAnalyser = ctx.createAnalyser();
  preGainAnalyser.fftSize = 256;
  activeMicSource.connect(preGainAnalyser);
  const _preBuf = new Uint8Array(preGainAnalyser.frequencyBinCount);
  let gateEnabled = false, gateOpen = true, gateThreshold = 0.03, lastVoiceMs = 0, gateHoldMs = 280;
  const GATE_SENS = { soft: { th: 0.02, hold: 420 }, medium: { th: 0.03, hold: 280 }, strong: { th: 0.05, hold: 170 } };
  const gateTimer = setInterval(() => {
    if (stopped) return;
    if (!gateEnabled) { if (gateMul !== 1) { gateMul = 1; gateOpen = true; applyMic(0.05); } return; }
    const lvl = _peak(preGainAnalyser, _preBuf);
    const now = Date.now();
    const openThresh = gateThreshold, closeThresh = gateThreshold * 0.6;
    if (lvl > openThresh) { lastVoiceMs = now; gateOpen = true; }
    else if (gateOpen && lvl < closeThresh && now - lastVoiceMs > gateHoldMs) { gateOpen = false; }
    const target = gateOpen ? 1 : 0;
    if (target !== gateMul) { gateMul = target; applyMic(gateOpen ? 0.015 : 0.14); }
  }, 40);
  // Keep the audio graph actively running. Some browsers won't "pull" a graph
  // that only feeds a MediaStreamDestination, which can leave the broadcast silent.
  // A zero-gain path to the real output forces processing without the host hearing themselves.
  const keepAlive = ctx.createGain();
  keepAlive.gain.value = 0;
  micGain.connect(keepAlive);
  keepAlive.connect(ctx.destination);

  // Headphone monitor: let the host hear the full mix (mic + callers) in their
  // headphones at an adjustable level. Defaults to 0 (off) to avoid speaker feedback.
  // Routed to a dedicated stream so the host can pick which output device it plays through.
  const monitorGain = ctx.createGain();
  monitorGain.gain.value = 0;
  micGain.connect(monitorGain);
  const callersMonitorGain = ctx.createGain();
  callersMonitorGain.gain.value = 1;
  callersBus.connect(callersMonitorGain);
  callersMonitorGain.connect(monitorGain);
  const monitorDest = ctx.createMediaStreamDestination();
  monitorGain.connect(monitorDest);
  const monitorAnalyser = ctx.createAnalyser();
  monitorAnalyser.fftSize = 256;
  monitorGain.connect(monitorAnalyser);
  const _monBuf = new Uint8Array(monitorAnalyser.frequencyBinCount);

  // Background music (station stream or uploaded music/sermon): played via a media
  // element, mixed into the broadcast (on-air toggle) and the host's monitor.
  const music = { el: null, source: null, chGain: null };
  let prevChGainRef = null;
  let musicBase = 0.5, musicDuckEnabled = true, musicDuckMul = 1, musicCallerActive = false;
  let musicTalkDuck = 0.35, musicCallerDuckMul = 0.18;
  const musicGain = ctx.createGain(); musicGain.gain.value = 0;
  const musicAirGain = ctx.createGain(); musicAirGain.gain.value = 1; // 1 = on-air, 0 = studio only
  musicGain.connect(musicAirGain); musicAirGain.connect(broadcastDelay); // -> listeners (through delay/dump)
  musicGain.connect(monitorGain); // -> host headphones
  // Feed the on-air music bed to phone callers too (so they hear the show under the host's voice).
  const confMusicGain = ctx.createGain();
  confMusicGain.gain.value = 1; // host-adjustable: how loud the bed sits for callers vs listeners
  const confMusicDest = ctx.createMediaStreamDestination();
  musicAirGain.connect(confMusicGain);
  confMusicGain.connect(confMusicDest);
  const musicAnalyser = ctx.createAnalyser(); musicAnalyser.fftSize = 256;
  musicGain.connect(musicAnalyser);
  const _musBuf = new Uint8Array(musicAnalyser.frequencyBinCount);
  const applyMusic = (smooth = 0.08) => {
    const g = Math.max(0, musicBase * musicDuckMul);
    try { musicGain.gain.setTargetAtTime(g, ctx.currentTime, smooth); } catch (e) { try { musicGain.gain.value = g; } catch (x) {} }
  };
  // Duck the music down while the host is speaking (or a caller is on the air).
  const musicDuckTimer = setInterval(() => {
    if (!music.el) { if (musicDuckMul !== 1) { musicDuckMul = 1; applyMusic(); } return; }
    let target = 1;
    if (musicCallerActive) target = musicCallerDuckMul; // a phone caller is live — keep music well under the conversation
    else if (musicDuckEnabled) {
      let lvl = 0; try { lvl = _peak(preGainAnalyser, _preBuf); } catch (e) {}
      target = lvl > 0.06 ? musicTalkDuck : 1;
    }
    if (Math.abs(target - musicDuckMul) > 0.01) { musicDuckMul = target; applyMusic(0.12); }
  }, 120);

  // iOS/Safari can't broadcast a Web Audio MediaStreamDestination (it comes out
  // silent), so on iOS we send the raw mic; other browsers broadcast the processed
  // mix so mic-level and call-in audio reach listeners. `dest` is always used for recording.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const bc = createBroadcaster(sid, isIOS ? micStream : dest.stream);

  const callers = new Map(); // callerId -> { pc, gain, comp, makeup, source }
  const phone = { source: null, gain: null, analyser: null, buf: null, stream: null }; // Twilio Voice conference bridge
  let after = null, stopped = false;
  let duckFloor = 0.45; // how far the mic dips while a caller speaks (lower = stronger)
  let autoLevel = true; // global default: gently normalise callers (compress loud + make-up quiet)
  let targetLoudness = "broadcast"; // makeup target the auto-level aims for
  const MAKEUP = { quiet: 1.35, broadcast: 1.7, loud: 2.1 };

  function applyAutoLevelTo(comp, makeup, on) {
    try {
      if (on) {
        comp.threshold.value = -28; comp.knee.value = 25; comp.ratio.value = 4; comp.attack.value = 0.01; comp.release.value = 0.25;
        makeup.gain.value = MAKEUP[targetLoudness] || 1.7;
      } else {
        comp.threshold.value = 0; comp.knee.value = 0; comp.ratio.value = 1; comp.attack.value = 0.003; comp.release.value = 0.25;
        makeup.gain.value = 1;
      }
    } catch (e) {}
  }
  function reapplyAllAutoLevel() { callers.forEach((e) => applyAutoLevelTo(e.comp, e.makeup, e.autoLevelOn)); }

  // Auto-duck: dip the host mic while a caller is speaking so voices sit on top.
  // Music guests relax the duck so live singing/instruments aren't squashed under the mic.
  const duckTimer = setInterval(() => {
    if (stopped) return;
    if (!autoDuck || callers.size === 0) { if (duckMul !== 1) { duckMul = 1; applyMic(); } return; }
    const speaking = _peak(callerAnalyser, _callerBuf) > 0.07;
    const anyMusic = Array.from(callers.values()).some((e) => e.music);
    const floor = anyMusic ? Math.max(duckFloor, 0.85) : duckFloor;
    const target = speaking ? floor : 1;
    if (target !== duckMul) { duckMul = target; applyMic(); }
  }, 120);

  function removeCaller(callerId) {
    const e = callers.get(callerId);
    if (!e) return;
    try { e.pc.close(); } catch (x) {}
    try { if (e.source) e.source.disconnect(); } catch (x) {}
    try { if (e.comp) e.comp.disconnect(); } catch (x) {}
    try { e.gain.disconnect(); } catch (x) {}
    try { if (e.makeup) e.makeup.disconnect(); } catch (x) {}
    try { if (e.monoGain) e.monoGain.disconnect(); } catch (x) {}
    try { if (e.delay) e.delay.disconnect(); } catch (x) {}
    try { if (e.merger) e.merger.disconnect(); } catch (x) {}
    try { if (e.widthGain) e.widthGain.disconnect(); } catch (x) {}
    try { if (e.audioEl) { e.audioEl.pause(); e.audioEl.srcObject = null; } } catch (x) {}
    callers.delete(callerId);
  }

  async function handleCallerOffer(callerId, offer) {
    removeCaller(callerId);
    const pc = new RTCPeerConnection(await iceConfig());
    // Caller chain: source -> comp (auto-level) -> gain (host trim) -> makeup -> [center | stereo widener] -> callersBus
    const comp = ctx.createDynamicsCompressor();
    const gain = ctx.createGain();
    const makeup = ctx.createGain();
    // Music mode widens a mono call into rich stereo via a short Haas delay on the right channel.
    const monoGain = ctx.createGain(); monoGain.gain.value = 1; // centered (talk) path
    const widthGain = ctx.createGain(); widthGain.gain.value = 0; // stereo (music) path
    const delay = ctx.createDelay(0.05); delay.delayTime.value = 0.012;
    const merger = ctx.createChannelMerger(2);
    const entry = { pc, gain, comp, makeup, monoGain, widthGain, delay, merger, source: null, audioEl: null, autoLevelOn: autoLevel, music: false };
    applyAutoLevelTo(comp, makeup, entry.autoLevelOn);
    comp.connect(gain);
    gain.connect(makeup);
    makeup.connect(monoGain); monoGain.connect(callersBus);
    makeup.connect(merger, 0, 0);
    makeup.connect(delay); delay.connect(merger, 0, 1);
    merger.connect(widthGain); widthGain.connect(callersBus);
    callers.set(callerId, entry);
    pc.ontrack = (e) => {
      // Feed caller audio into the broadcast mix...
      try { const src = ctx.createMediaStreamSource(e.streams[0]); src.connect(comp); entry.source = src; } catch (x) {}
      // ...and play it to the host's speakers so the host can hear the caller.
      try {
        const el = new Audio();
        el.srcObject = e.streams[0];
        el.autoplay = true;
        el.play().catch(() => {});
        entry.audioEl = el;
      } catch (x) {}
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) api.post(`/live/${sid}/signal`, { to: "caller", listener_id: callerId, from: "host", type: "ice", payload: e.candidate.toJSON() });
    };
    pc.onconnectionstatechange = () => { if (["failed", "closed", "disconnected"].includes(pc.connectionState)) removeCaller(callerId); };
    await pc.setRemoteDescription(offer);
    const ans = await pc.createAnswer();
    const ansSdp = preferStereoOpus(ans.sdp);
    await pc.setLocalDescription({ type: ans.type, sdp: ansSdp });
    await api.post(`/live/${sid}/signal`, { to: "caller", listener_id: callerId, from: "host", type: "answer", payload: { type: ans.type, sdp: ansSdp } });
  }

  async function poll() {
    if (stopped) return;
    try {
      const url = `/live/${sid}/signal?to=host-caller` + (after ? `&after=${after}` : "");
      const { data } = await api.get(url);
      for (const s of data) {
        after = s.id;
        if (s.type === "offer") await handleCallerOffer(s.listener_id, s.payload);
        else if (s.type === "ice" && callers.has(s.listener_id)) { try { await callers.get(s.listener_id).pc.addIceCandidate(s.payload); } catch (e) {} }
      }
    } catch (e) {}
    if (!stopped) setTimeout(poll, 1500);
  }
  poll();

  return {
    setOnCount(fn) { bc.setOnCount(fn); },
    getStream() { return dest.stream; },
    setMicLevel(v) { baseMic = Math.max(0, v); applyMic(); },
    setAutoDuck(on) { autoDuck = !!on; if (!autoDuck && duckMul !== 1) { duckMul = 1; applyMic(); } },
    setDuckDepth(floor) { duckFloor = Math.min(1, Math.max(0, floor)); },
    setAutoLevel(on) { autoLevel = !!on; callers.forEach((e) => { e.autoLevelOn = autoLevel; }); reapplyAllAutoLevel(); },
    setCallerAutoLevel(callerId, on) { const e = callers.get(callerId); if (e) { e.autoLevelOn = !!on; applyAutoLevelTo(e.comp, e.makeup, e.autoLevelOn); } },
    setCallerMusic(callerId, on) { const e = callers.get(callerId); if (e) { e.music = !!on; e.autoLevelOn = on ? false : autoLevel; applyAutoLevelTo(e.comp, e.makeup, e.autoLevelOn); try { const t = ctx.currentTime; e.widthGain.gain.setTargetAtTime(on ? 1 : 0, t, 0.05); e.monoGain.gain.setTargetAtTime(on ? 0 : 1, t, 0.05); } catch (x) {} } },
    setTargetLoudness(level) { if (MAKEUP[level]) { targetLoudness = level; reapplyAllAutoLevel(); } },
    getLoudness() {
      try {
        masterAnalyser.getByteTimeDomainData(_mixBuf);
        let sum = 0;
        for (let i = 0; i < _mixBuf.length; i++) { const v = (_mixBuf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / _mixBuf.length);
        if (rms < 1e-4) return -70;
        return Math.max(-70, 20 * Math.log10(rms));
      } catch (e) { return -70; }
    },
    isDucking() { return duckMul !== 1; },
    getLevel() { try { return _peak(analyser, _levelBuf); } catch (e) { return 0; } },
    getMixLevel() { try { return _peak(masterAnalyser, _mixBuf); } catch (e) { return 0; } },
    setMonitorLevel(v) { const g = Math.max(0, v); try { monitorGain.gain.setTargetAtTime(g, ctx.currentTime, 0.05); } catch (x) { try { monitorGain.gain.value = g; } catch (y) {} } },
    setNoiseGate(on) { gateEnabled = !!on; if (!gateEnabled && gateMul !== 1) { gateMul = 1; gateOpen = true; applyMic(0.05); } },
    setGateSensitivity(level) { const s = GATE_SENS[level]; if (s) { gateThreshold = s.th; gateHoldMs = s.hold; } },
    getMonitorLevel() { try { return _peak(monitorAnalyser, _monBuf); } catch (e) { return 0; } },
    getMonitorStream() { return monitorDest.stream; },
    switchMic(newStream) {
      try {
        const newSrc = ctx.createMediaStreamSource(newStream);
        newSrc.connect(micGain);
        newSrc.connect(preGainAnalyser);
        try { activeMicSource.disconnect(micGain); } catch (e) {}
        try { activeMicSource.disconnect(preGainAnalyser); } catch (e) {}
        try { activeMicStream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        activeMicSource = newSrc;
        activeMicStream = newStream;
        // On iOS the raw mic is what listeners receive, so hot-swap the outgoing track.
        if (isIOS && bc.replaceTrack) bc.replaceTrack(newStream.getAudioTracks()[0]);
        return true;
      } catch (e) { return false; }
    },
    setCallerLevel(callerId, v) { const e = callers.get(callerId); if (e) { try { e.gain.gain.value = Math.max(0, v); } catch (x) {} } },
    muteCaller(callerId, muted) { const e = callers.get(callerId); if (e) { e.gain.gain.value = muted ? 0 : 1; if (e.audioEl) e.audioEl.muted = muted; } },
    removeCaller,
    // Phone (Twilio Voice) callers: inject the conference remote MediaStream into the broadcast mix.
    addPhoneStream(stream) {
      try {
        if (phone.source) { try { phone.source.disconnect(); } catch (x) {} }
        const src = ctx.createMediaStreamSource(stream);
        const g = ctx.createGain(); g.gain.value = 1;
        const an = ctx.createAnalyser(); an.fftSize = 256;
        src.connect(g); g.connect(callersBus); g.connect(an);
        phone.source = src; phone.gain = g; phone.analyser = an; phone.buf = new Uint8Array(an.frequencyBinCount); phone.stream = stream;
        return true;
      } catch (e) { return false; }
    },
    setPhoneLevel(v) { if (phone.gain) { try { phone.gain.gain.value = Math.max(0, v); } catch (x) {} } },
    setCallersMonitor(v) { try { callersMonitorGain.gain.setTargetAtTime(Math.max(0, v), ctx.currentTime, 0.04); } catch (e) { try { callersMonitorGain.gain.value = Math.max(0, v); } catch (y) {} } },
    // Enable/disable the conference remote audio the host actually hears (Twilio SDK plays
    // it through the speaker directly, so a Web Audio gain can't silence it). Toggling the
    // remote track's `enabled` mutes it at the source — silencing both the host's headset
    // and the broadcast tap. Safe because waiting callers are muted in the conference, so
    // the remote stream only carries hold music when nobody is on air.
    setPhoneRemoteEnabled(on) { try { if (phone.stream) phone.stream.getAudioTracks().forEach((t) => { t.enabled = !!on; }); } catch (e) {} },
    getConfMusicStream() { try { return confMusicDest.stream; } catch (e) { return null; } },
    setCallersBroadcast(v) { try { callersBroadcastGain.gain.setTargetAtTime(Math.max(0, v), ctx.currentTime, 0.05); } catch (e) { try { callersBroadcastGain.gain.value = Math.max(0, v); } catch (y) {} } },
    setCallerMusicLevel(v) { try { confMusicGain.gain.setTargetAtTime(Math.max(0, v), ctx.currentTime, 0.05); } catch (e) { try { confMusicGain.gain.value = Math.max(0, v); } catch (y) {} } },
    getPhoneLevel() { try { return phone.analyser ? _peak(phone.analyser, phone.buf) : 0; } catch (e) { return 0; } },
    // Background music: play a station stream or an uploaded library track under the show.
    playBackgroundMusic(url, { loop = false, onEnded = null, fade = 1.2 } = {}) {
      try {
        const prev = music.el, prevSrc = music.source;
        const el = new Audio();
        el.crossOrigin = "anonymous";
        el.loop = !!loop;
        el.src = url;
        if (!loop && typeof onEnded === "function") el.onended = () => { try { onEnded(); } catch (e) {} };
        const src = ctx.createMediaElementSource(el);
        const chGain = ctx.createGain();
        chGain.gain.value = 0; // fade in
        src.connect(chGain); chGain.connect(musicGain);
        music.el = el; music.source = src; music.chGain = chGain;
        const p = el.play(); if (p && p.catch) p.catch(() => {});
        const t = ctx.currentTime;
        try { chGain.gain.setValueAtTime(0.0001, t); chGain.gain.linearRampToValueAtTime(1, t + Math.max(0.05, fade)); } catch (e) { chGain.gain.value = 1; }
        // Crossfade out the previous track, if any.
        if (prev) {
          try {
            const g = prevChGainRef;
            if (g) { g.gain.cancelScheduledValues(t); g.gain.setValueAtTime(g.gain.value, t); g.gain.linearRampToValueAtTime(0.0001, t + Math.max(0.05, fade)); }
          } catch (e) {}
          setTimeout(() => { try { prev.pause(); prev.src = ""; } catch (e) {} try { prevSrc.disconnect(); } catch (e) {} }, Math.max(60, fade * 1000) + 120);
        }
        prevChGainRef = chGain;
        musicDuckMul = 1; applyMusic(0.05);
        return true;
      } catch (e) { return false; }
    },
    stopBackgroundMusic({ fade = 1.0 } = {}) {
      const el = music.el, src = music.source, chGain = music.chGain;
      music.el = null; music.source = null; music.chGain = null; prevChGainRef = null;
      if (!el) return;
      try {
        const t = ctx.currentTime;
        if (chGain) { chGain.gain.cancelScheduledValues(t); chGain.gain.setValueAtTime(chGain.gain.value, t); chGain.gain.linearRampToValueAtTime(0.0001, t + Math.max(0.05, fade)); }
        setTimeout(() => { try { el.pause(); el.src = ""; } catch (e) {} try { src.disconnect(); } catch (e) {} }, Math.max(60, fade * 1000) + 120);
      } catch (e) { try { el.pause(); el.src = ""; } catch (x) {} try { src.disconnect(); } catch (x) {} }
    },
    isBackgroundPlaying() { return !!music.el; },
    setMusicLevel(v) { musicBase = Math.max(0, v); applyMusic(); },
    setMusicOnAir(on) { try { musicAirGain.gain.setTargetAtTime(on ? 1 : 0, ctx.currentTime, 0.05); } catch (e) { try { musicAirGain.gain.value = on ? 1 : 0; } catch (x) {} } },
    setMusicDuck(on) { musicDuckEnabled = !!on; if (!musicDuckEnabled && musicDuckMul !== 1) { musicDuckMul = 1; applyMusic(); } },
    setMusicCallerDuck(on) { musicCallerActive = !!on; },
    setMusicDuckDepths(talkMul, callerMul) {
      if (talkMul != null) musicTalkDuck = Math.max(0, Math.min(1, talkMul));
      if (callerMul != null) musicCallerDuckMul = Math.max(0, Math.min(1, callerMul));
      applyMusic(0.1);
    },
    getMusicLevel() { try { return music.el ? _peak(musicAnalyser, _musBuf) : 0; } catch (e) { return 0; } },
    removePhoneStream() {
      try { if (phone.source) phone.source.disconnect(); } catch (x) {}
      try { if (phone.gain) phone.gain.disconnect(); } catch (x) {}
      phone.source = null; phone.gain = null; phone.analyser = null; phone.stream = null;
    },
    setBroadcastDelay(seconds) { try { broadcastDelay.delayTime.setValueAtTime(Math.max(0, Math.min(20, seconds)), ctx.currentTime); } catch (e) {} },
    getBroadcastDelay() { try { return broadcastDelay.delayTime.value; } catch (e) { return 0; } },
    dumpBroadcast() {
      // Silence the delayed output for the buffered window so it never airs.
      try {
        const d = broadcastDelay.delayTime.value || 0;
        const t = ctx.currentTime;
        broadcastGain.gain.cancelScheduledValues(t);
        broadcastGain.gain.setValueAtTime(0, t);
        broadcastGain.gain.setValueAtTime(1, t + d + 0.25);
        return d;
      } catch (e) { return 0; }
    },
    stop() { stopped = true; clearInterval(duckTimer); clearInterval(gateTimer); clearInterval(musicDuckTimer); try { if (music.el) { music.el.pause(); music.el.src = ""; } } catch (e) {} try { if (music.source) music.source.disconnect(); } catch (e) {} try { if (phone.source) phone.source.disconnect(); } catch (e) {} bc.stop(); Array.from(callers.keys()).forEach(removeCaller); try { ctx.close(); } catch (e) {} },
  };
}

// Listener-as-caller: send mic into the broadcast (host mixes it in).
export function createCaller(sid, callerId, micStream, onState) {
  let after = null, stopped = false, pc = null;
  async function start() {
    pc = new RTCPeerConnection(await iceConfig());
    micStream.getTracks().forEach((t) => pc.addTrack(t, micStream));
    pc.onicecandidate = (e) => { if (e.candidate) api.post(`/live/${sid}/signal`, { to: "host-caller", listener_id: callerId, from: "caller", type: "ice", payload: e.candidate.toJSON() }); };
    pc.onconnectionstatechange = () => {
      if (!pc) return;
      if (pc.connectionState === "connected") onState && onState("connected");
      else if (["failed", "closed", "disconnected"].includes(pc.connectionState)) onState && onState("ended");
    };
    const offer = await pc.createOffer();
    const offerSdp = preferStereoOpus(offer.sdp);
    await pc.setLocalDescription({ type: offer.type, sdp: offerSdp });
    await api.post(`/live/${sid}/signal`, { to: "host-caller", listener_id: callerId, from: "caller", type: "offer", payload: { type: offer.type, sdp: offerSdp } });
    poll();
  }
  async function poll() {
    if (stopped || !pc) return;
    try {
      const url = `/live/${sid}/signal?to=caller&listener_id=${callerId}` + (after ? `&after=${after}` : "");
      const { data } = await api.get(url);
      for (const s of data) {
        after = s.id;
        if (s.type === "answer") await pc.setRemoteDescription(s.payload);
        else if (s.type === "ice") { try { await pc.addIceCandidate(s.payload); } catch (e) {} }
      }
    } catch (e) {}
    if (!stopped) setTimeout(poll, 1500);
  }
  start();
  return {
    setMuted(m) { micStream.getAudioTracks().forEach((t) => { t.enabled = !m; }); },
    stop() { stopped = true; if (pc) pc.close(); micStream.getTracks().forEach((t) => t.stop()); },
  };
}
